import React, { createContext, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { options } from '../options';
import { TYPE_COLLECTION, TYPE_DATE_COMPARISON, TYPE_BOOLEAN } from '../options/input-types';

import { apiFilterBuilder } from '../api-filter-builder';
import { legacyApiFilterBuilder } from '../legacy-api-filter-builder';

export const FiltersContext = createContext();

const updateFilters = (filters, newFilter) => {
  const { id, source } = newFilter;
  const currentFilterIndex = filters.findIndex((filter) => filter.id === id && filter.source === source);
  const newFilters =
    currentFilterIndex !== -1
      ? [...filters.slice(0, currentFilterIndex), ...filters.slice(currentFilterIndex + 1), newFilter]
      : [...filters, newFilter];
  return newFilters;
};

const filterShouldCombine = (id) => {
  // Return false for filters that don't exist in options
  if (options[id] == null) {
    return false;
  }
  const doNotCombineList = [TYPE_BOOLEAN, TYPE_DATE_COMPARISON];
  return !doNotCombineList.includes(options[id].type);
};

const generateApiFilters = (filters, useLegacyFilters = false, customFilterMap) => {
  // Filter out any filters that don't exist in options
  const validFilters = filters.filter((filter) => options[filter.id] != null);

  const filtersObject = validFilters.reduce((acc, filter) => {
    const { id, value, operator } = filter;
    if (acc[id] && filterShouldCombine(id)) {
      if (options[id].type === TYPE_COLLECTION) {
        return {
          ...acc,
          [id]: { id, value: [...acc[id].value, ...value], operator },
        };
      }
      if (operator.value && operator.value.toUpperCase() === acc[id].operator.value.toUpperCase()) {
        return {
          ...acc,
          [id]: { id, value: [...acc[id].value, ...value], operator },
        };
      }
    }
    return {
      ...acc,
      [id]: { id, value, operator },
    };
  }, {});
  const keys = Object.keys(filtersObject);
  const filterArrays = keys.map((key) => filtersObject[key]);
  return useLegacyFilters ? legacyApiFilterBuilder(filterArrays, customFilterMap) : apiFilterBuilder(filterArrays);
};

export const FiltersContextProvider = ({ children }) => {
  const [filters, setFilters] = useState([]);

  const updateFilter = useCallback(
    (id, value, operator, source) => {
      const newFilter = {
        id,
        value,
        operator,
        source,
      };
      const newFilters = updateFilters(filters, newFilter);
      setFilters(newFilters);
    },
    [filters, setFilters],
  );

  const bulkUpdateFilters = useCallback(
    (newFilters) => {
      const updatedFilters = newFilters.reduce(updateFilters, filters);
      setFilters(updatedFilters);
    },
    [setFilters, filters],
  );

  const resetFilters = useCallback(() => setFilters([]), [setFilters]);

  const buildApiFilters = useCallback(
    (useLegacyFilters = false, customFilterMap) => {
      return generateApiFilters(filters, useLegacyFilters, customFilterMap);
    },
    [filters],
  );

  const buildApiFiltersForSource = useCallback(
    (source, useLegacyFilters = false, customFilterMap) => {
      const filtersForSource = filters.filter((f) => f.source === source);
      return generateApiFilters(filtersForSource, useLegacyFilters, customFilterMap);
    },
    [filters],
  );

  const replaceFilters = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // `source` here can either be advanced search or the filter bar.
  const replaceFiltersForSource = useCallback((source, newFilters = []) => {
    setFilters((prevFilters) => {
      const filtersToKeep = prevFilters.reduce((acc, filter) => {
        if (filter.source !== source) {
          return [...acc, filter];
        }
        return [...acc];
      }, []);

      return [...filtersToKeep, ...newFilters];
    });
  }, []);

  const value = useMemo(
    () => ({
      filters,
      updateFilter,
      bulkUpdateFilters,
      resetFilters,
      buildApiFilters,
      buildApiFiltersForSource,
      replaceFilters,
      replaceFiltersForSource,
    }),
    [
      filters,
      updateFilter,
      bulkUpdateFilters,
      resetFilters,
      buildApiFilters,
      buildApiFiltersForSource,
      replaceFilters,
      replaceFiltersForSource,
    ],
  );

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
};

FiltersContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
